import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { AppointmentType } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/appointment_type.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { useModalControl, useAlert, useDebouncedValue } from '@frontend/design-system';
import { AppointmentTypeBulkColorAssignmentBanner } from '../../components';
import { AppointmentTypeGroupsTable } from '../../components/ServiceTypesV3/AppointmentTypeGroupsTable';
import {
  useAppointmentTypesCustomizationAndFeatureFlagsHelper,
  useCheckPaymentsEnabled,
  useSchedulingLocationInfo,
} from '../../hooks';
import { useServiceTypesHelperShallowStore } from '../../stores/use-service-types-helper-store';
import { PageConfig } from '../../types';

type ServiceTypeV3PageProps = {
  locationId?: string;
  isIntegratedOffice?: boolean;
};

export const ServiceTypeV3Page = ({ locationId, isIntegratedOffice = false }: ServiceTypeV3PageProps) => {
  const { t } = useTranslation('schedule');
  const alert = useAlert();
  const { selectedLocationIds, isMultiLocation } = useSchedulingLocationInfo();
  const selectedLocationId = locationId || selectedLocationIds[0];

  const { setPaymentsEnabled, setIsOnlineSchedulingEnabled } = useServiceTypesHelperShallowStore(
    'setPaymentsEnabled',
    'setIsOnlineSchedulingEnabled'
  );

  const [selectedServiceType, setSelectedServiceType] = useState<AppointmentType | null>(null);

  const { modalProps: editModalProps, triggerProps: editModalTriggerProps } = useModalControl();

  const [pageConfig, setPageConfig] = useState<PageConfig>({
    page: 1,
    limit: 50,
    searchValue: '',
  });

  const debouncedSearchValue = useDebouncedValue(pageConfig.searchValue, 400);

  const { mutateAsync: archiveAppointmentType } = SchedulerV3.Mutations.useUpdateAppointmentTypeMutation({
    optimisticUpdate: true,
  });

  const { data: serviceTypesData, isLoading: isLoadingAppointmentTypesData } =
    SchedulerV3.Helpers.useGetServiceTypesV3Data({
      debouncedSearchValue,
      pageConfig,
      selectedLocationId,
      selectedLocationIds: [selectedLocationId],
      childLocationId: isMultiLocation ? selectedLocationId : '',
      isIntegratedOffice,
    });

  const { isLoading: isLoadingCFInfo, isOnlineSchedulingEnabled } =
    useAppointmentTypesCustomizationAndFeatureFlagsHelper(selectedLocationId);

  const { isPaymentsEnabled } = useCheckPaymentsEnabled(selectedLocationId);

  useEffect(() => {
    setIsOnlineSchedulingEnabled(isOnlineSchedulingEnabled);
    setPaymentsEnabled(isPaymentsEnabled);
  }, [isPaymentsEnabled, isOnlineSchedulingEnabled]);

  const handleOnEdit = (appointmentTypeGroup: SchedulerV3.Types.AppointmentTypeGroup) => {
    setSelectedServiceType(appointmentTypeGroup.appointmentType);
    editModalTriggerProps.onClick();
  };

  const handleOnDelete = (appointmentTypeGroup: SchedulerV3.Types.AppointmentTypeGroup) => {
    const { appointmentType } = appointmentTypeGroup;
    setSelectedServiceType(appointmentTypeGroup.appointmentType);
    archiveAppointmentType({
      appointmentTypeId: appointmentType.id,
      childLocationId: isMultiLocation ? appointmentType.locationId : undefined,
      hidden: true,
      details: {
        ...appointmentType.details,
        act: !appointmentType.details?.act,
        sobs: false,
      },
    })
      .then((res) => {
        if (res) {
          alert.success(
            t('Appointment type {{status}} successfully', {
              status: !!appointmentType.details?.act ? 'archived' : 'unarchived',
            })
          );
        }
      })
      .catch(() => {
        alert.error(
          t('Failed to {{status}} appointment type', {
            status: !!appointmentType.details?.act ? 'archive' : 'unarchive',
          })
        );
      });
  };

  const editModalOnClose = () => {
    setSelectedServiceType(null);
    editModalProps.onClose();
  };

  const isLoading = isLoadingAppointmentTypesData || isLoadingCFInfo;
  return (
    <section css={containerStyle}>
      <AppointmentTypeBulkColorAssignmentBanner locationIds={[selectedLocationId]} />
      <div css={{ margin: theme.spacing(3) }}>
        <AppointmentTypeGroupsTable
          isIntegratedOffice={isIntegratedOffice}
          locationId={selectedLocationId}
          serviceTypesData={serviceTypesData.appointmentTypeGroups ?? []}
          totalCount={serviceTypesData?.totalAppointmentTypeGroupCount || 0}
          isLoading={isLoading}
          pageConfig={pageConfig}
          setPageConfig={setPageConfig}
          onEdit={handleOnEdit}
          onDelete={handleOnDelete}
          isPaymentsEnabled={isPaymentsEnabled}
          debouncedSearchValue={debouncedSearchValue}
          isMultiLocation={isMultiLocation}
        />
        {selectedServiceType && (
          // TODO: Add the tray here for editing/adding service types
          <div onClick={editModalOnClose}></div>
        )}
      </div>
    </section>
  );
};

const containerStyle = css({
  position: 'relative',
  background: theme.colors.white,
  borderRadius: theme.borderRadius.medium,
  boxShadow: theme.shadows.light,
  overflow: 'auto',
});
